import { useEffect } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useReactFlow,
  type Node,
  type Edge,
  type NodeTypes,
} from "@xyflow/react";
import { LocateFixed, Minus, Plus } from "lucide-react";
import "@xyflow/react/dist/style.css";

type ColorMode = "light" | "dark";

export function TopologyFlowCanvas({
  nodes,
  edges,
  nodeTypes,
  colorMode,
  fitDep,
  focusNodeId,
  height = 760,
  onNodeClick,
  onPaneClick,
  onNodeMouseEnter,
  onNodeMouseMove,
  onNodeMouseLeave,
  onEdgeMouseEnter,
  onEdgeMouseLeave,
  emptyMessage = "No topology nodes available.",
}: {
  nodes: Node[];
  edges: Edge[];
  nodeTypes?: NodeTypes;
  colorMode: ColorMode;
  fitDep: string;
  focusNodeId?: string;
  height?: number;
  onNodeClick?: (event: React.MouseEvent, node: Node) => void;
  onPaneClick?: () => void;
  onNodeMouseEnter?: (event: React.MouseEvent, node: Node) => void;
  onNodeMouseMove?: (event: React.MouseEvent, node: Node) => void;
  onNodeMouseLeave?: (event: React.MouseEvent, node: Node) => void;
  onEdgeMouseEnter?: (event: React.MouseEvent, edge: Edge) => void;
  onEdgeMouseLeave?: (event: React.MouseEvent, edge: Edge) => void;
  emptyMessage?: string;
}) {
  return (
    <div style={{ height }} className="rounded-2xl">
      {nodes.length > 0 ? (
        <ReactFlow
          key={fitDep}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{
            padding: 0.25,
            minZoom: 0.15,
            maxZoom: 1.2,
          }}
          proOptions={{ hideAttribution: true }}
          colorMode={colorMode}
          nodesDraggable
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onNodeMouseEnter={onNodeMouseEnter}
          onNodeMouseMove={onNodeMouseMove}
          onNodeMouseLeave={onNodeMouseLeave}
          onEdgeMouseEnter={onEdgeMouseEnter}
          onEdgeMouseLeave={onEdgeMouseLeave}
        >
          <Background gap={24} size={1} color="rgb(var(--line))" />
          <Controls position="bottom-right" showInteractive={false} />
          <FlowToolbar />
          <MiniMap
            pannable
            zoomable
            maskColor="rgba(125,108,255,0.08)"
            nodeColor={(n) => {
              const d = (n.data as { device?: { status?: string } } | undefined)
                ?.device;
              if (!d) return "rgb(var(--fg-mute))";
              if (d.status === "online") return "rgb(99,255,200)";
              if (d.status === "alerting") return "rgb(255,180,120)";
              if (d.status === "offline") return "rgb(255,122,168)";
              return "rgb(var(--fg-mute))";
            }}
          />
          <ViewportAutoFit dep={fitDep} focusNodeId={focusNodeId} />
        </ReactFlow>
      ) : (
        <div className="flex h-full items-center justify-center text-sm text-fg-mute">
          {emptyMessage}
        </div>
      )}
    </div>
  );
}

function ViewportAutoFit({
  dep,
  focusNodeId,
}: {
  dep: string;
  focusNodeId?: string;
}) {
  const rf = useReactFlow();

  useEffect(() => {
    const fitOpts = {
      padding: 0.2,
      minZoom: 0.1,
      maxZoom: 1.2,
      duration: 350,
    };
    const focus = () => {
      const focusNode = focusNodeId ? rf.getNode(focusNodeId) : undefined;
      if (!focusNode) {
        rf.fitView(fitOpts);
        return;
      }
      rf.setCenter(focusNode.position.x, focusNode.position.y, {
        zoom: 0.78,
        duration: 350,
      });
    };
    const raf = window.requestAnimationFrame(focus);
    const t = window.setTimeout(focus, 250);
    return () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [dep, focusNodeId, rf]);

  return null;
}

function FlowToolbar() {
  const rf = useReactFlow();

  return (
    <div className="pointer-events-auto absolute left-3 top-3 z-20 inline-flex items-center gap-1 rounded-xl border border-line/80 bg-bg-0/90 p-1.5 shadow-[0_10px_24px_-18px_rgba(0,0,0,0.75)] backdrop-blur">
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
        aria-label="Zoom in"
        title="Zoom in"
        onClick={() => rf.zoomIn({ duration: 180 })}
      >
        <Plus size={14} />
      </button>
      <button
        type="button"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
        aria-label="Zoom out"
        title="Zoom out"
        onClick={() => rf.zoomOut({ duration: 180 })}
      >
        <Minus size={14} />
      </button>
      <button
        type="button"
        className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[10px] font-semibold text-fg-mute transition-colors hover:bg-surface-2 hover:text-fg"
        aria-label="Fit all nodes"
        title="Fit all nodes"
        onClick={() =>
          rf.fitView({
            padding: 0.2,
            minZoom: 0.1,
            maxZoom: 1.2,
            duration: 250,
          })
        }
      >
        <LocateFixed size={12} />
        Fit
      </button>
    </div>
  );
}
